import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import ReceiptDocument, { RECEIPT_CSS, RECEIPT_W, RECEIPT_H } from "@/components/ReceiptDocument";
import { printReceipt } from "@/lib/printReceipt";
import { rupeesInWords } from "@/lib/receiptWords";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Printer } from "lucide-react";
import { toast } from "sonner";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const selCls = "w-full bg-[#121212] border-0 border-b border-white/20 text-white font-mont text-sm py-2 focus:outline-none focus:border-[#D4AF37]";
const taCls = "w-full bg-transparent border-0 border-b border-white/20 px-0 py-2 text-white font-mont text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#D4AF37] resize-none";
const panelCls = "border border-white/10 bg-[#0F0F0F] p-5 space-y-4";
const MODES = ["Cash", "Cheque", "UPI", "Bank Transfer", "Card", "Other"];
const MM_PX = 96 / 25.4;

const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default function ReceiptEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { profiles, activeProfileId } = useProfiles();

  const [receiptId, setReceiptId] = useState(id || "");
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadedId = useRef("");
  const savingRef = useRef(false); // blocks a second click even within the same instant

  // load an existing receipt, or start a fresh one
  useEffect(() => {
    if (id) {
      if (loadedId.current === id) return;
      loadedId.current = id;
      setReceiptId(id);
      api.get(`/receipts/${id}`)
        .then(({ data }) => { setForm(data); setDirty(false); })
        .catch((err) => {
          toast.error(err?.response?.data?.detail || "Could not open this receipt");
          navigate("/receipts", { replace: true });
        });
      return;
    }
    const pid = activeProfileId || profiles[0]?.id;
    if (!pid || form) return;
    setForm({
      profile_id: pid,
      kind: sp.get("kind") === "voucher" ? "voucher" : "receipt",
      receipt_no: "",
      date: todayLocal(),
      party_name: "",
      amount: "",
      mode: "Cash",
      mode_ref: "",
      purpose: "",
      txn_id: "",
      show_signature_image: false,
    });
    // eslint-disable-next-line
  }, [id, activeProfileId, profiles]);

  const profile = profiles.find((p) => p.id === form?.profile_id);

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  // scale the A5 card to fit the preview column
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, el.clientWidth / (RECEIPT_W * MM_PX)));
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", fit);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", fit); };
  }, [form === null]); // eslint-disable-line

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const current = () => ({ ...form, amount: Number(form.amount) || 0 });

  const save = async () => {
    if (saving || savingRef.current) return;
    if (!form.profile_id) { toast.error("No business selected"); return; }
    if (!form.party_name.trim()) { toast.error(form.kind === "voucher" ? "Enter who the payment was made to" : "Enter who the money was received from"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Enter the amount"); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = current();
      let data;
      if (receiptId) {
        ({ data } = await api.put(`/receipts/${receiptId}`, payload));
      } else {
        ({ data } = await api.post("/receipts", payload));
      }
      setForm(data);
      setDirty(false);
      toast.success(receiptId ? "Saved" : `Saved (${data.receipt_no})`);
      if (!receiptId) {
        setReceiptId(data.id);
        loadedId.current = data.id;
        navigate(`/receipts/${data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const goBack = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    navigate("/receipts");
  };

  if (!form) return <div className="p-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">Loading…</div>;

  const isVoucher = form.kind === "voucher";
  const words = rupeesInWords(form.amount);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <style>{RECEIPT_CSS}</style>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <button onClick={goBack} className="flex items-center gap-2 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-white">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Receipts
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="font-mont text-[10px] uppercase tracking-widest text-[#D4AF37]">Unsaved changes</span>}
          <Button data-testid="save-receipt-btn" onClick={save} disabled={saving} className="rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button data-testid="print-receipt-btn" onClick={() => printReceipt(current(), profile)} variant="outline" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print / PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6 items-start">
        <aside className="space-y-5">
          <div className={panelCls}>
            <div className="font-serif text-lg text-white">{isVoucher ? "Payment Voucher" : "Payment Receipt"}</div>
            {!form.txn_id && (
              <div>
                <Label className={labelCls}>Type</Label>
                <select data-testid="receipt-kind" value={form.kind} onChange={(e) => update({ kind: e.target.value })} className={selCls}>
                  <option value="receipt">Payment Receipt — money received</option>
                  <option value="voucher">Payment Voucher — money paid</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Number</Label>
                <Input value={form.receipt_no} onChange={(e) => update({ receipt_no: e.target.value })} placeholder="Auto on save" className={fieldCls} />
              </div>
              <div>
                <Label className={labelCls}>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => update({ date: e.target.value })} className={fieldCls + " [color-scheme:dark]"} />
              </div>
            </div>
          </div>

          <div className={panelCls}>
            <div>
              <Label className={labelCls}>{isVoucher ? "Paid to" : "Received from"}</Label>
              <Input data-testid="receipt-party" value={form.party_name} onChange={(e) => update({ party_name: e.target.value })} className={fieldCls} />
            </div>
            <div>
              <Label className={labelCls}>Amount (₹)</Label>
              <Input data-testid="receipt-amount" type="number" min="0" step="any" value={form.amount} onChange={(e) => update({ amount: e.target.value })} className={fieldCls} />
              {words && <p className="font-mont text-[10px] text-neutral-500 mt-1.5 leading-relaxed">{words}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Paid by</Label>
                <select value={form.mode} onChange={(e) => update({ mode: e.target.value })} className={selCls}>
                  {MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
              </div>
              <div>
                <Label className={labelCls}>{form.mode === "Cheque" ? "Cheque no." : "Reference no."}</Label>
                <Input value={form.mode_ref} onChange={(e) => update({ mode_ref: e.target.value })} placeholder="Optional" className={fieldCls} />
              </div>
            </div>
            <div>
              <Label className={labelCls}>For (purpose)</Label>
              <textarea data-testid="receipt-purpose" rows={3} value={form.purpose} onChange={(e) => update({ purpose: e.target.value })} className={taCls} placeholder="e.g. Advance towards Flat A-12" />
              <p className={`font-mont text-[10px] mt-1 ${form.purpose.length > 190 ? "text-[#D4AF37]" : "text-neutral-600"}`}>
                {form.purpose.length > 190 ? "Long text prints in smaller letters — about two lines fit best." : "Two lines fit on the card."}
              </p>
            </div>
            {profile?.signature_url && (
              <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={!!form.show_signature_image} onChange={(e) => update({ show_signature_image: e.target.checked })} />
                Print saved signature image
              </label>
            )}
          </div>
          {form.txn_id && <p className="font-mont text-[10px] text-neutral-600 leading-relaxed">This {isVoucher ? "voucher" : "receipt"} is linked to a transaction in Finance.</p>}
        </aside>

        <section className="min-w-0">
          <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">— Preview (A5, prints landscape)</div>
          <div ref={wrapRef} className="w-full">
            <div style={{ width: `${RECEIPT_W * MM_PX * scale}px`, height: `${RECEIPT_H * MM_PX * scale}px`, boxShadow: "0 0 0 1px rgba(255,255,255,0.12)", background: "#fff" }}>
              <div style={{ width: `${RECEIPT_W}mm`, height: `${RECEIPT_H}mm`, transform: `scale(${scale})`, transformOrigin: "top left" }}>
                <ReceiptDocument receipt={current()} profile={profile} />
              </div>
            </div>
          </div>
          <p className="font-mont text-[11px] text-neutral-600 mt-4 leading-relaxed">
            Click <span className="text-neutral-300">Print / PDF</span> and choose “Save as PDF” (or your printer). Paper size is set automatically to A5 landscape; if your printer shows A4, choose “Fit to page”.
          </p>
        </section>
      </div>
    </div>
  );
}
